import { Link } from 'react-router-dom';

const LAST_UPDATED = 'July 2026';

const sections = [
  {
    title: '1. Introduction',
    body: `SublingoX ("we", "our", or "the App") is committed to protecting your privacy. This Privacy Policy explains what information we collect, how we use it, and your rights regarding that information. By using the App, you agree to the practices described in this policy.`,
  },
  {
    title: '2. Information We Collect',
    items: [
      { label: 'Account information', detail: 'If you register, we collect the name and email address you provide through Firebase Authentication. When you use Google or Apple sign-in, your provider may supply a display name and email address, including an Apple private relay address if you choose Hide My Email. Guest users are not required to provide personal information.' },
      { label: 'Language preferences', detail: 'Your chosen native and target languages are stored locally in your browser\'s localStorage. If you are signed in, these preferences may be saved to your account to enable cross-device sync.' },
      { label: 'Usage data', detail: 'We do not currently use analytics or tracking services. No behavioral data (pages visited, clicks, session duration) is collected or sent to external servers.' },
      { label: 'Device data', detail: 'Standard browser information (such as browser type and language) may be received as part of normal web requests, but is not stored or profiled.' },
    ],
  },
  {
    title: '3. How We Use Your Information',
    body: `We use your information solely to provide and improve the App's features — including personalising content to your selected languages, maintaining your session, and enabling account-based preference sync. We do not sell, rent, or share your personal data with third parties for marketing purposes.`,
  },
  {
    title: '4. Firebase Authentication',
    body: `Email/password, Google, and Apple authentication are handled by Firebase Authentication. Google, Apple, and Firebase may process authentication and technical data under their respective privacy policies. SublingoX receives the account identifier and profile information made available through Firebase; we do not receive your Google or Apple password.`,
  },
  {
    title: '5. Text-to-Speech',
    body: `The pronunciation feature uses the Web Speech API built into your browser. Speech synthesis is performed entirely on your device — no text is sent to SublingoX servers for TTS processing. Depending on your browser and OS, your browser vendor may process TTS data according to their own privacy policies.`,
  },
  {
    title: '6. Cookies and Local Storage',
    body: `We use your browser's localStorage to store your language preferences and session information. We do not use advertising cookies or third-party tracking cookies. You can clear localStorage at any time through your browser settings, which will reset your preferences.`,
  },
  {
    title: '7. Data Retention',
    body: `Guest data stored in localStorage is retained until you clear your browser data. Account data is retained as long as your account exists. You may request deletion of your account and associated data at any time by contacting us.`,
  },
  {
    title: '8. Children\'s Privacy',
    body: `SublingoX is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us and we will delete it promptly.`,
  },
  {
    title: '9. Your Rights',
    body: `Depending on your location, you may have rights under GDPR, CCPA, or other applicable laws — including the right to access, correct, or delete your personal data. To exercise these rights, contact us through the App or at the email address associated with your account.`,
  },
  {
    title: '10. Changes to This Policy',
    body: `We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top. Your continued use of the App after changes constitutes acceptance of the updated policy. We encourage you to review this page periodically.`,
  },
  {
    title: '11. Contact Us',
    body: `If you have any questions or concerns about this Privacy Policy or how your data is handled, please contact us through the App's feedback channels or at the email address associated with your account.`,
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12">
      <div className="mx-auto max-w-3xl">

        {/* Header */}
        <div className="mb-10">
          <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-4xl font-black text-slate-900">Privacy Policy</h1>
          <p className="mt-2 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>
          <p className="mt-4 text-slate-600">
            Your privacy matters to us. This policy explains in plain language what data SublingoX collects, why, and how it is used.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((s) => (
            <section key={s.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-3 text-lg font-bold text-slate-800">{s.title}</h2>
              {s.body && <p className="text-sm leading-relaxed text-slate-600">{s.body}</p>}
              {s.items && (
                <ul className="space-y-3">
                  {s.items.map((item) => (
                    <li key={item.label} className="flex gap-2 text-sm">
                      <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-indigo-400 mt-1.5" />
                      <span className="text-slate-600">
                        <span className="font-semibold text-slate-800">{item.label}: </span>
                        {item.detail}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* Footer nav */}
        <div className="mt-10 flex flex-col items-center gap-2 text-center text-xs text-slate-400">
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-indigo-600 hover:underline">Terms &amp; Conditions</Link>
            <Link to="/credits" className="hover:text-indigo-600 hover:underline">Credits</Link>
            <Link to="/" className="hover:text-indigo-600 hover:underline">Home</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} SublingoX · Subram Miles</p>
        </div>
      </div>
    </div>
  );
}
